// widget.js — Reavil embeddable widget (closed Shadow DOM)
// Features:
//  - Reads project_id from <script data-project="..."> or URL param ?projectId=xxx
//  - API_URL = https://api.reavil.io
//  - Brand color: #0FA2B9 (overridable via project config or data-color attr)
//  - Icon+text (default) or icon-only (mobile WebView or data-type attr)
//  - Draggable floating launcher with dismissible button
//  - Modal UI with 5 emojis and feedback form
//  - WebView support: React Native, iOS WebKit, Android, Flutter
//  - autoOpen mode for native app integration
//  - keepalive fetch for POST
//  - No global CSS (all in Shadow DOM closed)
//  - Self-contained, safe for any host site
(function () {
  // ---------- CONFIG ----------
  const API_URL = 'https://api.reavil.io';
  const DEFAULT_COLOR = "#0FA2B9";
  const EMOJIS = ["😡", "😞", "😐", "🙂", "🤩"];
  // Updated SVG dimensions to match final floating button styling
  const LOGO_SVG = `<svg width="32" height="30" viewBox="0 0 74 68" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M25.3864 31C30.5148 31 34.6721 27.2584 34.6721 22.6428C34.6721 18.0273 30.5148 14.2857 25.3864 14.2857C20.2581 14.2857 16.1007 18.0273 16.1007 22.6428C16.1007 27.2584 20.2581 31 25.3864 31Z" fill="currentColor"/>
  <path d="M43.9579 32.8572C43.9579 37.9855 47.6995 42.1429 52.315 42.1429C56.9305 42.1429 60.6721 37.9855 60.6721 32.8572C60.6721 27.7288 56.9305 23.5715 52.315 23.5715C47.6995 23.5715 43.9579 27.7288 43.9579 32.8572Z" fill="currentColor"/>
  <path d="M25.0521 36.8948C19.9268 37.0734 15.9023 40.9575 16.063 45.5703C16.2238 50.183 20.5089 53.7776 25.6342 53.599C30.7594 53.4204 34.7839 49.5362 34.6232 44.9235C34.4625 40.3108 30.1773 36.7162 25.0521 36.8948Z" fill="currentColor"/>
  <path d="M43.9579 21.7143C43.9579 21.7143 41.7912 5 24.4579 5C7.12452 5 4.95786 21.7143 4.95786 21.7143" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M53.2436 53.2857C53.2436 53.2857 69.9579 51.119 69.9579 33.7857C69.9579 16.4524 53.2436 14.2857 53.2436 14.2857" stroke="currentColor" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M6.81516 46.8215C6.81516 46.8215 9.5626 63.4502 26.8854 62.8465C44.2082 62.2429 45.7915 45.4633 45.7915 45.4633" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

  // ---------- UTIL ----------
  const qAll = (sel, root = document) => Array.from((root || document).querySelectorAll(sel));
  const el = (tag, attrs = {}, children = []) => {
    const n = document.createElement(tag);
    for (const k in attrs) {
      if (k === "style") n.setAttribute("style", attrs[k]);
      else if (k === "html") n.innerHTML = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    (Array.isArray(children) ? children : [children]).forEach((c) => {
      if (c === null || c === undefined) return;
      n.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return n;
  };

  // find the last script with data-project (strict)
  function getWidgetScript() {
    const scripts = document.querySelectorAll('script[data-project]');
    return scripts[scripts.length - 1] || null;
  }

  // Check URL params first for project ID (supports autoOpen mode)
  const urlParams = new URLSearchParams(window.location.search);
  const urlProjectId = urlParams.get('projectId') || urlParams.get('projectid') || urlParams.get('project_id');

  const scriptEl = getWidgetScript();
  let projectAttr = null;
  let dataTypeAttr = null;
  let dataColorAttr = null;

  // If URL has projectId, use it (autoOpen mode), otherwise require script tag
  if (urlProjectId) {
    projectAttr = urlProjectId;
  } else {
    if (!scriptEl) {
      console.error("Reavil widget: script tag with data-project not found. Add <script data-project=\"...\"> to page or pass ?projectId=xxx in URL.");
      return;
    }
    projectAttr = scriptEl.getAttribute("data-project");
    dataTypeAttr = scriptEl.getAttribute("data-type"); // optional override: "icon" or "full"
    dataColorAttr = scriptEl.getAttribute("data-color"); // optional override
  }

  const project_id = projectAttr && projectAttr.length ? ( /^\d+$/.test(projectAttr) ? Number(projectAttr) : projectAttr ) : null;
  if (!project_id) {
    console.error("Reavil widget: invalid project ID.");
    return;
  }

  // WebView detection
  const isWebView = /(WebView|wv|Version\/.*Chrome)/.test(navigator.userAgent) || window.ReactNativeWebView || window.webkit?.messageHandlers || window.AndroidInterface;
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobileWebView = isWebView && (isIOS || isAndroid);

  // Dismissible button state
  let dismissTimer = null;
  let dismissDelay = 20000; // Start at 20 seconds
  let isDismissed = false;

  // Check for autoOpen URL parameter (already parsed urlParams above)
  const autoOpen = urlParams.get('autoOpen') === 'true' || urlParams.get('autoopen') === 'true';

  // ---------- CREATE HOST & SHADOW ----------
  const host = el("div");
  function attachHost() {
    try { document.body.appendChild(host); }
    catch (e) { window.addEventListener("DOMContentLoaded", () => document.body.appendChild(host)); }
  }
  attachHost();

  const shadow = host.attachShadow({ mode: "closed" });

  // ---------- STYLES & FONT (Poppins inside shadow) ----------
  const css = `
    /* Base font-size assumed 16px -> rem conversions */
    /* Inline Poppins latin subset weights 400/600/700 */
    @font-face { font-family: 'Poppins'; font-style: normal; font-weight: 400; font-display: swap; src: url(https://fonts.gstatic.com/s/poppins/v24/pxiEyp8kv8JHgFVrJJfecnFHGPc.woff2) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122; }
    @font-face { font-family: 'Poppins'; font-style: normal; font-weight: 600; font-display: swap; src: url(https://fonts.gstatic.com/s/poppins/v24/pxiByp8kv8JHgFVrLEj6Z1xlFd2JQEk.woff2) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122; }
    @font-face { font-family: 'Poppins'; font-style: normal; font-weight: 700; font-display: swap; src: url(https://fonts.gstatic.com/s/poppins/v24/pxiByp8kv8JHgFVrLCz7Z1xlFd2JQEk.woff2) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122; }
    :host {
      all: initial;
      font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
      /* Safe area insets for iOS notches and Android gestures */
      --rv-safe-area-top: env(safe-area-inset-top, 0px);
      --rv-safe-area-bottom: env(safe-area-inset-bottom, 0px);
      --rv-safe-area-left: env(safe-area-inset-left, 0px);
      --rv-safe-area-right: env(safe-area-inset-right, 0px);
      /* Scale variables */
      --rv-color-primary: ${dataColorAttr || DEFAULT_COLOR};
      --rv-space-xxs: 0.25rem;  /* 4px */
      --rv-space-xs: 0.5rem;    /* 8px */
      --rv-space-sm: 0.75rem;   /* 12px */
      --rv-space-md: 1rem;      /* 16px */
      --rv-space-lg: 1.5rem;    /* 24px */
      --rv-space-xl: 2rem;      /* 32px */
      --rv-space-2xl: 2.5rem;   /* 40px */
      --rv-space-3xl: 3rem;     /* 48px */
      --rv-space-4xl: 3.5rem;   /* 56px */
      --rv-radius-sm: 0.5rem;
      --rv-radius-pill: 62.5rem; /* ~999px large pill */
      --rv-size-launcher: 4.5rem;
        --rv-border-1: 0.0625rem; /* 1px */
      --rv-size-launcher-full-h: 3.875rem;
      --rv-size-logo: 2.375rem;
      --rv-size-close: 3rem;
      --rv-size-emoji: 3.5rem;
      --rv-size-emoji-mobile: 3rem;
      --rv-font-emoji: 2rem;
      --rv-font-emoji-mobile: 1.75rem;
      --rv-modal-width: 25rem;
      --rv-border-thin: 0.09375rem; /* 1.5px */
      --rv-shadow-primary-sm: 0 0.625rem 1.875rem rgba(15,162,185,0.16);
      --rv-shadow-primary-btn: 0 0.375rem 1.25rem rgba(15,162,185,0.2);
      --rv-shadow-modal: 0 1.5rem 5rem rgba(2,6,23,0.18);
      --rv-shadow-close: 0 0.375rem 1.125rem rgba(15,23,42,0.08);
      --rv-shadow-emoji-hover: 0 0.5rem 1.25rem rgba(0,0,0,0.08);
      --rv-shadow-emoji-active: 0 0.75rem 1.75rem rgba(0,0,0,0.12);
      --rv-focus-ring: 0 0 0 0.375rem rgba(15,162,185,0.06);
      --rv-translate-keyframe: var(--rv-space-md);
      --rv-translate-emoji-hover: -0.25rem; /* -4px */
      --rv-translate-emoji-selected: -0.375rem; /* -6px */
      --rv-offset-launcher: 1.25rem; /* 20px */
    }

    /* Floating container with fade-in */
    .rv-floating { position: fixed; right: calc(var(--rv-offset-launcher) + var(--rv-safe-area-right)); bottom: calc(var(--rv-offset-launcher) + var(--rv-safe-area-bottom)); z-index: 2147483647; display: flex; gap: var(--rv-space-sm); align-items: center; opacity:0; transition: opacity .28s ease; }
    .rv-floating.rv-visible { opacity:1; }

    /* Floating button (transplanted final styling) */
    .rv-btn-circle {
      width: var(--rv-size-launcher); height: var(--rv-size-launcher); border-radius: var(--rv-radius-pill);
      display:flex; align-items:center; justify-content:center;
      background: var(--rv-color-primary); color:#fff; border:none;
      box-shadow: var(--rv-shadow-primary-sm);
      cursor: grab; transition: transform .12s ease, box-shadow .12s ease;
    }
    .rv-btn-circle:active { transform: scale(.98); cursor: grabbing; }

    .rv-btn-full {
      height: var(--rv-size-launcher-full-h); padding: 0 1.125rem; border-radius: var(--rv-radius-pill);
      display:flex; align-items:center; gap: var(--rv-space-sm);
      background: var(--rv-color-primary); color:#fff; border:none;
      font-weight:600; font-size:0.875rem; box-shadow: var(--rv-shadow-primary-sm); cursor: grab;
    }
    .rv-btn-full:active { transform: scale(.99); cursor: grabbing; }

    .rv-logo-wrap {
      width: var(--rv-size-logo); height: var(--rv-size-logo); border-radius: var(--rv-radius-sm);
      display:flex; align-items:center; justify-content:center; background: var(--rv-color-primary); color:#fff; flex-shrink:0;
    }

    .rv-label {
      color:#fff; font-weight:700; font-size:0.8125rem; line-height:1.05; text-align:left;
      display:flex; flex-direction:column;
    }
    .rv-label .line1, .rv-label .line2 { white-space:nowrap; }

    /* Backdrop & modal */
    .rv-backdrop { position: fixed; inset: 0; display:none; align-items:center; justify-content:center; background: rgba(2,6,23,0.48); backdrop-filter: blur(0.125rem); z-index:2147483646; padding: var(--rv-safe-area-top) var(--rv-safe-area-right) var(--rv-safe-area-bottom) var(--rv-safe-area-left); }
    .rv-backdrop.show { display:flex; }

    .rv-modal {
      width: var(--rv-modal-width); max-width: calc(100% - var(--rv-space-2xl)); background:#fff; border-radius:0; padding: var(--rv-space-xl) var(--rv-space-2xl) var(--rv-space-lg);
      box-shadow: var(--rv-shadow-modal); position: relative; animation: slideUp .28s ease;
    }
    @keyframes slideUp { from { opacity:0; transform: translateY(var(--rv-translate-keyframe)); } to { opacity:1; transform:translateY(0);} }

    .rv-close { position: absolute; left:50%; transform:translateX(-50%); top: -1.5rem; width: var(--rv-size-close); height: var(--rv-size-close); border-radius: var(--rv-radius-pill);
      display:flex; align-items:center; justify-content:center; background:#fff; border:none; box-shadow: var(--rv-shadow-close); cursor:pointer; font-weight:700; }

    .rv-heading { text-align:center; font-size:1.5rem; font-weight:600; color:#0f172a; margin-bottom:1.5rem; line-height:1.25; }
    .rv-heading span { display:block; }

    .rv-error { display:none; background:#fef2f2; color:#991b1b; border:var(--rv-border-1) solid #fecaca; padding:0.75rem 1rem; border-radius:0.5rem; margin-bottom:1rem; font-size:0.875rem; }
    .rv-error.show { display:block; }

    .rv-emoji-row { display:flex; gap: var(--rv-space-md); justify-content:center; margin-bottom: var(--rv-space-lg); }
    .rv-emoji {
      width: var(--rv-size-emoji); height: var(--rv-size-emoji); border-radius:50%; display:flex; align-items:center; justify-content:center; font-size: var(--rv-font-emoji);
      background:#f8fafc; border:0.125rem solid transparent; cursor:pointer; transition: transform .14s ease, box-shadow .14s ease, background .14s ease;
    }
    .rv-emoji:hover { transform: translateY(var(--rv-translate-emoji-hover)); box-shadow: var(--rv-shadow-emoji-hover); background:#f1f5f9; }
    .rv-emoji.selected { transform: translateY(var(--rv-translate-emoji-selected)) scale(1.1); box-shadow: var(--rv-shadow-emoji-active); background:#fff; border-color: var(--rv-color-primary); }

    .rv-anonymous-note { text-align:center; font-size:0.8125rem; color:#64748b; margin-bottom: var(--rv-space-lg); display:flex; align-items:center; justify-content:center; gap:0.375rem; }
    .rv-anonymous-note svg { width:0.875rem; height:0.875rem; flex-shrink:0; }

    .rv-textarea { width:100%; min-height:15.5rem; padding:1.25rem; border-radius:1rem; border: var(--rv-border-thin) solid #e6eef3; font-size:0.9375rem; color:#0f172a; resize:vertical; outline:none; box-sizing:border-box; }
    .rv-textarea::placeholder { color:#94a3b8; }
    .rv-textarea:focus { box-shadow: var(--rv-focus-ring); border-color: var(--rv-color-primary); }

    .rv-submit-wrap { display:flex; justify-content:center; margin-top: var(--rv-space-lg); }
    .rv-submit { background: var(--rv-color-primary); color:#fff; border:none; padding:0.75rem 3rem; border-radius: var(--rv-radius-pill); font-size:1rem; font-weight:700; cursor:pointer; box-shadow: var(--rv-shadow-primary-btn); transition: background 0.3s ease, transform 0.2s ease; }
    .rv-submit:disabled { opacity:0.6; cursor:not-allowed; transform:none; }
    .rv-submit.success { background: #10b981; transform: scale(1.05); }

    .rv-footer { text-align:center; margin-top:1.125rem; color:#64748b; font-size:0.8125rem; }
    .rv-footer a { color: var(--rv-color-primary); font-weight:600; text-decoration:none; }

    .rv-bottom { height:0.5rem; background: var(--rv-color-primary); border-radius:0; margin: var(--rv-space-lg) calc(var(--rv-space-2xl) * -1) calc(var(--rv-space-lg) * -1); }
    .rv-hidden { visibility:hidden; }

    @media (max-width: 560px) {
      :host {
        --rv-size-launcher: 2.625rem; /* 42px - much smaller button */
        --rv-size-launcher-full-h: 2.5rem; /* 40px - smaller full button height */
        --rv-size-logo: 1.625rem; /* 26px - smaller logo */
        --rv-offset-launcher: 0.75rem; /* 12px - closer to edge */
        --rv-modal-width: 20rem; /* 320px max width */
        --rv-size-close: 2.25rem; /* 36px - smaller close button */
        --rv-size-emoji: 2.5rem; /* 40px - smaller emojis */
        --rv-font-emoji: 1.5rem; /* 24px - smaller emoji font */
      }
      .rv-btn-circle { width: var(--rv-size-launcher) !important; height: var(--rv-size-launcher) !important; }
      .rv-btn-full { height: var(--rv-size-launcher-full-h) !important; padding: 0 0.625rem !important; font-size: 0.75rem !important; gap: 0.375rem !important; }
      .rv-logo-wrap { width: var(--rv-size-logo) !important; height: var(--rv-size-logo) !important; }
      .rv-logo-wrap svg { width: 18px !important; height: 16px !important; }
      .rv-label { font-size: 0.625rem !important; }
      .rv-modal { width: var(--rv-modal-width) !important; max-width: 90% !important; padding: 1rem 0.875rem 0.75rem !important; }
      .rv-close { width: var(--rv-size-close) !important; height: var(--rv-size-close) !important; top: -1.125rem !important; font-size: 1rem !important; }
      .rv-heading { font-size: 1.125rem !important; margin-bottom: 1rem !important; line-height: 1.3 !important; }
      .rv-error { font-size: 0.8125rem !important; padding: 0.625rem 0.75rem !important; margin-bottom: 0.75rem !important; }
      .rv-emoji-row { gap: 0.5rem !important; margin-bottom: 1rem !important; }
      .rv-emoji { width: var(--rv-size-emoji) !important; height: var(--rv-size-emoji) !important; font-size: var(--rv-font-emoji) !important; border-width: 0.0625rem !important; }
      .rv-anonymous-note { font-size: 0.75rem !important; margin-bottom: 0.875rem !important; }
      .rv-textarea { width: 100% !important; min-height: 7.5rem !important; padding: 0.875rem !important; font-size: 0.875rem !important; border-radius: 0.75rem !important; box-sizing: border-box !important; }
      .rv-submit-wrap { margin-top: 1rem !important; }
      .rv-submit { padding: 0.625rem 2rem !important; font-size: 0.875rem !important; }
      .rv-footer { font-size: 0.75rem !important; margin-top: 0.875rem !important; }
      .rv-bottom { height: 0.375rem !important; margin: 1rem -0.875rem -0.75rem !important; }
    }

    @media (max-width: 380px) {
      :host {
        --rv-size-launcher: 2.625rem; /* 42px - even smaller */
        --rv-size-logo: 1.375rem; /* 22px */
        --rv-modal-width: 18rem; /* 288px */
        --rv-size-emoji: 2.25rem; /* 36px */
        --rv-font-emoji: 1.375rem; /* 22px */
      }
      .rv-btn-circle { width: var(--rv-size-launcher) !important; height: var(--rv-size-launcher) !important; }
      .rv-logo-wrap { width: var(--rv-size-logo) !important; height: var(--rv-size-logo) !important; }
      .rv-logo-wrap svg { width: 16px !important; height: 14px !important; }
      .rv-heading { font-size: 1rem !important; }
      .rv-emoji-row { gap: 0.375rem !important; }
      .rv-emoji { width: var(--rv-size-emoji) !important; height: var(--rv-size-emoji) !important; font-size: var(--rv-font-emoji) !important; }
      .rv-anonymous-note { font-size: 0.6875rem !important; }
      .rv-textarea { min-height: 6.5rem !important; padding: 0.75rem !important; font-size: 0.8125rem !important; }-rv-font-emoji) !important; }
      .rv-textarea { min-height: 6.5rem !important; padding: 0.75rem !important; font-size: 0.8125rem !important; }
      .rv-submit { padding: 0.5rem 1.5rem !important; font-size: 0.8125rem !important; }
    }
  `;

  // Redundant font load: link PLUS @import (some CSPs block @import)
  // Add font with error handling for WebView restrictions
  const fontLink = el('link', { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap' });
  fontLink.onerror = () => {
    console.warn('[Reavil] Google Fonts blocked, using system fonts');
  };
  shadow.appendChild(fontLink);
  const styleEl = el('style', { html: css });
  shadow.appendChild(styleEl);

  // ---------- BUILD DOM INSIDE SHADOW ----------
  const floating = el("div", { class: "rv-floating rv-hidden" });

  // circle button with dismiss
  const btnCircleWrap = el("div", { style: "position: relative; display: none;" });
  const btnCircle = el("button", { class: "rv-btn-circle", type: "button", title: "Give feedback", "aria-label": "Give feedback" });
  const logoWrapCircle = el("div", { class: "rv-logo-wrap", html: LOGO_SVG });
  btnCircle.appendChild(logoWrapCircle);
  btnCircleWrap.appendChild(btnCircle);

  // full button with dismiss (two-line label)
  const btnFullWrap = el("div", { style: "position: relative; display: none;" });
  const btnFull = el("button", { class: "rv-btn-full", type: "button", title: "Give feedback", "aria-label": "Give feedback" });
  const logoWrapFull = el("div", { class: "rv-logo-wrap", html: LOGO_SVG });
  const label = el("div", { class: "rv-label" });
  const line1 = el("span", { class: "line1", html: "What are you" });
  const line2 = el("span", { class: "line2", html: "struggling with?" });
  label.appendChild(line1);
  label.appendChild(line2);
  btnFull.appendChild(logoWrapFull);
  btnFull.appendChild(label);
  btnFullWrap.appendChild(btnFull);

  // Auto icon-only for mobile WebView, or use dataTypeAttr override
  // If autoOpen mode, hide floating button entirely
  if (autoOpen) {
    btnCircleWrap.style.display = "none";
    btnFullWrap.style.display = "none";
  } else {
    const shouldShowIcon = isMobileWebView || dataTypeAttr === "icon";
    if (shouldShowIcon) {
      btnCircleWrap.style.display = "flex";
      btnFullWrap.style.display = "none";
    } else {
      btnCircleWrap.style.display = "none";
      btnFullWrap.style.display = "flex";
    }
  }

  floating.appendChild(btnCircleWrap);
  floating.appendChild(btnFullWrap);
  shadow.appendChild(floating);

  // Backdrop & modal
  const backdrop = el("div", { class: "rv-backdrop", role: "dialog", "aria-hidden": "true" });
  const modal = el("div", { class: "rv-modal", role: "document" });

  const closeBtn = el("button", { class: "rv-close", type: "button", "aria-label": "Close" }, "✕");
  const heading = el("div", { class: "rv-heading" });
  heading.innerHTML = '<span class="rv-head-line1">Help us know what you</span><span class="rv-head-line2">are struggling with?</span>';
  const errorBox = el("div", { class: "rv-error", id: "rvError" });
  const emojiRow = el("div", { class: "rv-emoji-row" });
  EMOJIS.forEach((em) => {
    const n = el("div", { class: "rv-emoji", "data-emoji": em, title: em }, em);
    emojiRow.appendChild(n);
  });

  const anonymousNote = el("div", { class: "rv-anonymous-note" });
  anonymousNote.innerHTML = '<svg viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/></svg><span>Your feedback is anonymous</span>';

  const textarea = el("textarea", { class: "rv-textarea", "aria-label": "Feedback message", placeholder: "Your feedback helps us fix what's broken and make you happy in return..." });
  const submitWrap = el("div", { class: "rv-submit-wrap" });
  const submitBtn = el("button", { class: "rv-submit", type: "button" }, "Submit");

  const footer = el("div", { class: "rv-footer", html: 'Powered by: <a href="https://reavil.io" target="_blank" rel="noreferrer">Reavil.io</a>' });
  const bottomBar = el("div", { class: "rv-bottom" });

  submitWrap.appendChild(submitBtn);
  modal.appendChild(closeBtn);
  modal.appendChild(heading);
  modal.appendChild(errorBox);
  modal.appendChild(emojiRow);
  modal.appendChild(anonymousNote);
  modal.appendChild(textarea);
  modal.appendChild(submitWrap);
  modal.appendChild(footer);
  modal.appendChild(bottomBar);
  backdrop.appendChild(modal);
  shadow.appendChild(backdrop);

  // ---------- INTERACTIONS ----------
  let selectedEmoji = null;
  const emojiNodes = qAll(".rv-emoji", shadow);

  function clearEmojiSelection() {
    emojiNodes.forEach((n) => n.classList.remove("selected"));
    selectedEmoji = null;
  }
  function selectEmojiNode(node) {
    clearEmojiSelection();
    node.classList.add("selected");
    selectedEmoji = node.getAttribute("data-emoji");
  }

  // click handlers
  btnCircle.addEventListener("click", openModal);
  btnFull.addEventListener("click", openModal);
  closeBtn.addEventListener("click", closeModal);
  backdrop.addEventListener("click", (ev) => { if (ev.target === backdrop) closeModal(); });
  submitBtn.addEventListener("click", handleSubmit);
  textarea.addEventListener("keydown", (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleSubmit(); });

  emojiNodes.forEach((n) => n.addEventListener("click", () => selectEmojiNode(n)));

  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeModal(); });

  // Android back button support
  if (isAndroid && isWebView) {
    window.addEventListener('popstate', (e) => {
      if (e.state?.reavilModal) {
        e.preventDefault();
        closeModal();
      }
    });
  }

  // open/close functions
  function openModal() {
    backdrop.classList.add("show");
    backdrop.setAttribute("aria-hidden", "false");
    textarea.value = "";
    clearEmojiSelection();
    errorBox.classList.remove("show");
    
    // Notify native app that modal opened
    notifyNative('modalOpened', { projectId: project_id });
    
    // Add to history for Android back button
    if (isAndroid && isWebView) {
      history.pushState({ reavilModal: true }, '');
    }
    
    // Keyboard handling for mobile
    if (isWebView) {
      setTimeout(() => {
        textarea.focus();
        // Scroll textarea into view on mobile keyboards
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } else {
      textarea.focus();
    }
  }
  function closeModal() {
    backdrop.classList.remove("show");
    backdrop.setAttribute("aria-hidden", "true");
    errorBox.classList.remove("show");
    
    // Notify native app that modal closed
    notifyNative('modalClosed', { projectId: project_id });
    
    // In autoOpen mode, signal native app to dismiss WebView
    if (autoOpen && isWebView) {
      setTimeout(() => {
        notifyNative('dismissWebView', { projectId: project_id, reason: 'closed' });
      }, 300);
    }
    
    // Remove from history if added
    if (isAndroid && isWebView && history.state?.reavilModal) {
      history.back();
    }
  }

  // nice inline error
  let errTimer = null;
  function showError(msg) {
    errorBox.textContent = msg;
    errorBox.classList.add("show");
    if (errTimer) clearTimeout(errTimer);
    errTimer = setTimeout(() => errorBox.classList.remove("show"), 4200);
  }

  // draggable logic for visible button (works for both variants)
  (function makeDraggable() {
    let dragging = false;
    let offsetX = 0;
    let offsetY = 0;

    const start = (e) => {
      const t = visibleButton();
      dragging = true;
      const rect = t.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      offsetX = clientX - rect.left;
      offsetY = clientY - rect.top;
      t.style.transition = "none";
    };
    const move = (e) => {
      if (!dragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const x = clientX - offsetX;
      const y = clientY - offsetY;
      const t = visibleButton();
      const rect = t.getBoundingClientRect();
      const safeX = Math.min(Math.max(8, x), window.innerWidth - rect.width - 8);
      const safeY = Math.min(Math.max(8, y), window.innerHeight - rect.height - 8);
      t.style.position = "fixed";
      const rootFs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      t.style.left = (safeX / rootFs) + "rem";
      t.style.top = (safeY / rootFs) + "rem";
      // detach floating from anchored right/bottom
      floating.style.right = "auto";
      floating.style.bottom = "auto";
    };
    const end = () => {
      dragging = false;
      const t = visibleButton();
      if (t) t.style.transition = "all .12s ease";
    };

    const visibleButton = () => (btnFullWrap.style.display !== "none" ? btnFullWrap : btnCircleWrap);

    [btnCircle, btnFull].forEach((b) => {
      b.addEventListener("mousedown", start);
      b.addEventListener("touchstart", start, { passive: true });
    });
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", end);
    window.addEventListener("touchend", end);
  })();

  // ---------- NATIVE BRIDGE COMMUNICATION ----------
  function notifyNative(event, data) {
    try {
      const payload = { event, data, timestamp: Date.now() };
      
      // React Native
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify(payload));
      }
      
      // iOS WebKit
      if (window.webkit?.messageHandlers?.ReavilWidget) {
        window.webkit.messageHandlers.ReavilWidget.postMessage(payload);
      }
      
      // Android
      if (window.AndroidInterface?.onReavilEvent) {
        window.AndroidInterface.onReavilEvent(JSON.stringify(payload));
      }
      
      // Flutter
      if (window.FlutterChannel?.postMessage) {
        window.FlutterChannel.postMessage(JSON.stringify(payload));
      }
    } catch (e) {
      // Silently fail if no native bridge available
    }
  }

  // ---------- LOAD PROJECT CONFIG ----------
  (function initConfigResolution() {
    const cacheKey = `reavil_cfg_${project_id}`;
    const defaultColor = dataColorAttr || DEFAULT_COLOR;
    const defaultVariant = dataTypeAttr || 'full';
    let revealed = false;

    function applyConfig(color, variant, reveal) {
      host.style.setProperty('--rv-color-primary', color);
      const finalVariant = isMobileWebView ? 'icon' : variant;
      if (finalVariant === 'icon') { 
        btnFullWrap.style.display = 'none'; 
        btnCircleWrap.style.display = 'flex'; 
      } else { 
        btnFullWrap.style.display = 'flex'; 
        btnCircleWrap.style.display = 'none'; 
      }
      if (reveal && !revealed && !autoOpen) {
        floating.classList.remove('rv-hidden');
        floating.classList.add('rv-visible');
        revealed = true;
      }
    }

    // Try cache first with localStorage fallback
    let cached = null;
    let storageAvailable = true;
    try { 
      cached = JSON.parse(localStorage.getItem(cacheKey) || 'null'); 
    } catch (e) { 
      cached = null;
      storageAvailable = false;
      console.warn('[Reavil] localStorage unavailable in WebView, using defaults');
    }
    if (cached && typeof cached === 'object') {
      const cColor = cached.color || cached.primary_color || cached.accentColor || cached.accent_color || defaultColor;
      const cVariant = cached.variant || defaultVariant;
      applyConfig(cColor, cVariant, true);
    } else {
      applyConfig(defaultColor, defaultVariant, true);
    }

    // Fetch network config
    (async function fetchNetwork() {
      let resolvedColor = defaultColor;
      let resolvedVariant = defaultVariant;
      try {
        const res = await fetch(`${API_URL}/v1/projects/${encodeURIComponent(project_id)}/config`);
        if (res.ok) {
          const body = await res.json().catch(() => null);
            if (body && body.config) {
              const cfg = body.config || {};
              resolvedColor = cfg.primaryColor || cfg.primary_color || cfg.color || cfg.accentColor || cfg.accent_color || cfg.button_color || resolvedColor;
              resolvedVariant = cfg.buttonVariant || cfg.widgetVariant || cfg.button || resolvedVariant;
            }
        }
      } catch (e) { /* ignore */ }
      applyConfig(resolvedColor, resolvedVariant, true);
      // Persist to cache if available
      if (storageAvailable) {
        try { localStorage.setItem(cacheKey, JSON.stringify({ color: resolvedColor, variant: resolvedVariant, ts: Date.now() })); } catch (e) { /* ignore */ }
      }
    })();
  })();

  // ---------- NETWORK DETECTION ----------
  function isOnline() {
    return navigator.onLine !== false;
  }

  window.addEventListener('offline', () => {
    console.warn('[Reavil] Network offline detected');
  });
  
  window.addEventListener('online', () => {
    console.log('[Reavil] Network online restored');
  });

  // ---------- SUBMIT ----------
  async function handleSubmit() {
    errorBox.classList.remove("show");
    
    // Check network connectivity
    if (!isOnline()) {
      showError("No internet connection. Please check your network and try again.");
      return;
    }
    
    const text = (textarea.value || "").trim();
    if (!text) { showError("Please describe the issue."); return; }
    if (text.length < 20) { showError("Please provide at least 20 characters of feedback."); return; }
    if (!selectedEmoji) { showError("Choose an emoji that best matches your feeling."); return; }

    const payload = {
      project_id,
      text,
      emoji: selectedEmoji,
      metadata: {
        url: window.location.href,
        title: document.title,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        custom: window.REAVIL_CUSTOM_METADATA || {}
      }
    };

    submitBtn.disabled = true;
    const prev = submitBtn.textContent;
    submitBtn.textContent = "Sending…";

    try {
      const res = await fetch(`${API_URL}/v1/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-project-key": String(projectAttr || "")
        },
        body: JSON.stringify(payload),
        keepalive: true
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        let errorMsg = "Failed to send feedback.";
        
        if (body.error === "rate_limit_exceeded" || res.status === 429) {
          errorMsg = "You're submitting too quickly. Please wait a moment and try again.";
        } else if (res.status === 401) {
          errorMsg = "Unable to submit. Please check your connection and try again.";
        } else if (res.status >= 500) {
          errorMsg = "Server error. Please try again in a moment.";
        } else if (body.error || body.message) {
          errorMsg = body.message || body.error;
        }
        submitBtn.textContent = prev;
        submitBtn.disabled = false;
        showError(errorMsg);
        return;
      }
      submitBtn.classList.add("success");
      submitBtn.textContent = "Submitted ✓";
      
      notifyNative('feedbackSubmitted', { feedbackId: body.feedback_id, projectId: project_id });
      
      setTimeout(() => {
        submitBtn.classList.remove("success");
        submitBtn.textContent = prev;
        submitBtn.disabled = false;
        textarea.value = "";
        clearEmojiSelection();
        closeModal();
        
        if (autoOpen && isWebView) {
          setTimeout(() => {
            notifyNative('dismissWebView', { projectId: project_id, reason: 'submitted' });
          }, 300);
        }
      }, 1500);
    } catch (err) {
      submitBtn.textContent = prev;
      submitBtn.disabled = false;
      
      notifyNative('feedbackError', { error: err.message, projectId: project_id });
      
      const errorMsg = !isOnline() 
        ? "No internet connection. Please check your network and try again."
        : "Unable to connect. Please check your internet connection and try again.";
      showError(errorMsg);
    }
  }

  // Auto-open modal if autoOpen parameter is set
  if (autoOpen) {
    setTimeout(() => {
      openModal();
    }, 100);
  }

  // Expose programmatic API
  try {
    window.ReavilWidget = window.ReavilWidget || {};
    window.ReavilWidget.open = openModal;
    window.ReavilWidget.close = closeModal;
    window.ReavilWidget.version = 'b1-webview';
    window.ReavilWidget.isWebView = isWebView;
    window.ReavilWidget.platform = isIOS ? 'ios' : isAndroid ? 'android' : 'web';
    window.ReavilWidget.autoOpen = autoOpen;
    window.ReavilWidget.getProjectId = () => project_id;
    window.ReavilWidget.notifyNative = notifyNative;
  } catch (e) { /* ignore */ }

  // Done: widget is attached & behavior wired.
})();
